import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import {
  staffActivitiesService, staffTagsService, staffPartnersService,
  partnerActivitiesService, tagsService, partnersService, mediaUrl,
  staffVolunteerTeamsService, staffCoordinatorsService, staffUsersService,
} from '../../lib/api-service';
import { getTextColorHex } from '../../utils/color-contrast';
import { PartnerMultiSelect } from '../../components/PartnerAutocomplete';
import type { Tag, StaffActivity, VolunteerTeam, Coordinator, StaffUser } from '../../lib/types';
import { IconPicker, IconByName } from '../../components/IconPicker';
import { MarkdownHelpButton } from '../../components/MarkdownHelp';
import { toLocalDatetime } from '../../utils/date';
import { LocationPicker } from '../../components/LocationPicker';
import { Plus, Trash2, Loader2, Eye, EyeOff, Save, ArrowLeft, Users, ArrowUp, ArrowDown, Lock } from 'lucide-react';
import { DateTimeInput } from '../../components/DateInput';

const DESC_TEMPLATE = `## Despre activitate

## Cine poate participa

## Ce trebuie să știi

## Contact
`;

const FIELD_LABELS: Record<string, string> = {
  title: 'Titlu',
  description: 'Descriere',
  start_datetime: 'Data & ora start',
  end_datetime: 'Data & ora final',
  location: 'Locație pe hartă',
  location_text: 'Locație',
  banner_image: 'Banner',
  tag_ids: 'Tag-uri',
  activity_partners_data: 'Prieteni',
  contacts_data: 'Contacte',
  participant_limit: 'Limită participanți',
  min_age: 'Vârstă minimă',
  max_age: 'Vârstă maximă',
  minors_require_adult: 'Minorii necesită adult',
  requires_registration: 'Necesită înregistrare',
  is_invitation_only: 'Doar cu invitație',
  registration_type: 'Tip înregistrare',
  registration_deadline: 'Deadline înregistrare',
  registration_link: 'Link înregistrare externă',
  registration_fields: 'Câmpuri formular înregistrare',
  volunteer_applications_open: 'Aplicații voluntari',
  volunteer_capacity: 'Capacitate voluntari',
  volunteer_guidance: 'Instrucțiuni voluntari',
  is_volunteer_visible: 'Vizibil pentru voluntari',
  is_published: 'Publicat',
};

function flattenErrorValue(value: unknown): string[] {
  if (typeof value === 'string') return [value];
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)];
  if (Array.isArray(value)) return value.flatMap(flattenErrorValue);
  if (value && typeof value === 'object') return Object.values(value).flatMap(flattenErrorValue);
  return [];
}

function formatApiError(body: unknown, status?: number): string {
  const fallback = 'Eroare la salvare. Verifică datele și încearcă din nou.';
  if (!body) {
    if (status === 413) return 'Fișierul este prea mare.';
    if (status === 401 || status === 403) return 'Nu ai permisiunea pentru această acțiune.';
    if (status && status >= 500) return 'Eroare de server. Încearcă din nou mai târziu.';
    return fallback;
  }
  if (typeof body === 'string') return body;
  if (typeof body !== 'object') return fallback;

  const obj = body as Record<string, unknown>;
  if (typeof obj.detail === 'string') return obj.detail;

  const parts: string[] = [];
  for (const [key, value] of Object.entries(obj)) {
    const messages = flattenErrorValue(value);
    if (messages.length === 0) continue;
    if (key === 'non_field_errors' || key === 'detail') {
      parts.push(messages.join(' '));
    } else {
      const label = FIELD_LABELS[key] ?? key;
      parts.push(`${label}: ${messages.join(' ')}`);
    }
  }
  return parts.length > 0 ? parts.join(' • ') : fallback;
}

interface PartnerOption { id: number; name: string; slug: string }
interface PartnerEntry { partner_id: number; contact_name: string; contact_phone: string; contact_email: string }
interface ContactEntry { name: string; phone: string; email: string }
interface ParticipantTypeEntry {
  id?: number;
  title: string;
  description: string;
  limit: string;
  price: string;
  is_default: boolean;
  sort_order: number;
  confirmed_count: number;
}

export default function ActivityForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const isPartnerView = location.pathname.startsWith('/staff/activitatile-mele');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Options
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [allPartners, setAllPartners] = useState<PartnerOption[]>([]);

  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(DESC_TEMPLATE);
  const [startDatetime, setStartDatetime] = useState('');
  const [endDatetime, setEndDatetime] = useState('');
  const [locationText, setLocationText] = useState('');
  const [locationCoords, setLocationCoords] = useState<{ lng: number; lat: number } | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [partnerEntries, setPartnerEntries] = useState<PartnerEntry[]>([]);
  const [contactEntries, setContactEntries] = useState<ContactEntry[]>([]);
  const [participantLimit, setParticipantLimit] = useState('');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [minorsRequireAdult, setMinorsRequireAdult] = useState(false);
  const [requiresRegistration, setRequiresRegistration] = useState(false);
  const [isInvitationOnly, setIsInvitationOnly] = useState(false);
  const [registrationType, setRegistrationType] = useState<'internal' | 'external'>('internal');
  const [registrationDeadline, setRegistrationDeadline] = useState('');
  const [registrationLink, setRegistrationLink] = useState('');
  const [registrationFields, setRegistrationFields] = useState<Record<string, { visible: boolean; required: boolean }>>({
    phone: { visible: true, required: false },
    date_of_birth: { visible: true, required: false },
    gender: { visible: true, required: false },
    source_organization: { visible: true, required: false },
  });
  const [registrationCount, setRegistrationCount] = useState(0);
  const [participantTypes, setParticipantTypes] = useState<ParticipantTypeEntry[]>([]);
  const [paymentMethods, setPaymentMethods] = useState('');
  const [volunteerApplicationsOpen, setVolunteerApplicationsOpen] = useState(false);
  const [volunteerCapacity, setVolunteerCapacity] = useState('');
  const [volunteerGuidance, setVolunteerGuidance] = useState('');
  const [activitySlug, setActivitySlug] = useState('');
  const [volTeams, setVolTeams] = useState<VolunteerTeam[]>([]);
  const [coordinators, setCoordinators] = useState<Coordinator[]>([]);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamIcon, setNewTeamIcon] = useState('');
  const [newTeamColor, setNewTeamColor] = useState('#3b0b86');
  const [newTeamCapacity, setNewTeamCapacity] = useState('');
  const [coordSearch, setCoordSearch] = useState('');
  const [coordDropdownOpen, setCoordDropdownOpen] = useState(false);
  const [isVolunteerVisible, setIsVolunteerVisible] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        const [tagsRes, partnersRes] = await Promise.all([
          isPartnerView ? tagsService.list() : staffTagsService.list(),
          isPartnerView ? partnersService.list() : staffPartnersService.list(),
        ]);
        setAllTags(tagsRes.results);
        setAllPartners(partnersRes.results);

        if (isEdit) {
          const a = isPartnerView
            ? await partnerActivitiesService.detail(id!)
            : await staffActivitiesService.detail(id!);
          setTitle(a.title);
          setDescription(a.description);
          setStartDatetime(toLocalDatetime(a.start_datetime));
          setEndDatetime(toLocalDatetime(a.end_datetime));
          setLocationText(a.location_text);
          if (a.location?.coordinates) {
            setLocationCoords({ lng: a.location.coordinates[0], lat: a.location.coordinates[1] });
          }
          setBannerPreview(mediaUrl(a.banner_image) || null);
          setSelectedTagIds(a.tags.map((t) => t.id));
          setPartnerEntries(
            a.partners.map((p) => ({
              partner_id: p.id,
              contact_name: p.contact_name,
              contact_phone: p.contact_phone,
              contact_email: p.contact_email,
            }))
          );
          setContactEntries(a.contacts.map((c) => ({ name: c.name, phone: c.phone, email: c.email })));
          setParticipantLimit(a.participant_limit?.toString() || '');
          setMinAge(a.min_age?.toString() || '');
          setMaxAge(a.max_age?.toString() || '');
          setMinorsRequireAdult(a.minors_require_adult);
          setRequiresRegistration(a.requires_registration);
          setIsInvitationOnly(a.is_invitation_only ?? false);
          setRegistrationType(a.registration_type || 'internal');
          setRegistrationDeadline(toLocalDatetime(a.registration_deadline));
          setRegistrationLink(a.registration_link);
          if (a.registration_fields && Object.keys(a.registration_fields).length > 0) {
            setRegistrationFields((prev) => ({ ...prev, ...a.registration_fields }));
          }
          setRegistrationCount(a.registration_count ?? 0);
          setParticipantTypes(
            (a.participant_types ?? [])
              .slice()
              .sort((x, y) => x.sort_order - y.sort_order)
              .map((t, idx) => ({
                id: t.id,
                title: t.title,
                description: t.description,
                limit: t.limit?.toString() ?? '',
                price: t.price ?? '0',
                is_default: t.is_default,
                sort_order: idx,
                confirmed_count: t.confirmed_count ?? 0,
              }))
          );
          setPaymentMethods(a.payment_methods ?? '');
          setVolunteerApplicationsOpen(a.volunteer_applications_open ?? false);
          setVolunteerCapacity(a.volunteer_capacity?.toString() || '');
          setVolunteerGuidance(a.volunteer_guidance ?? '');
          setActivitySlug(a.slug);
          setIsVolunteerVisible(a.is_volunteer_visible ?? false);
          setIsPublished(a.is_published);
          setIsFeatured(a.is_featured ?? false);

          // Load volunteer teams, coordinators, and staff users for coordinator selection
          if (!isPartnerView) {
            const [teamsRes, coordRes, usersRes] = await Promise.all([
              staffVolunteerTeamsService.list(a.slug),
              staffCoordinatorsService.list(a.slug),
              staffUsersService.list({ page: 1 }),
            ]);
            setVolTeams(teamsRes.results);
            setCoordinators(coordRes.results);
            setStaffUsers(usersRes.results);
          }
        }
      } catch {
        setError('Eroare la încărcarea datelor.');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [id, isEdit, isPartnerView]);

  function handleBannerChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  }

  function addPartner() {
    setPartnerEntries([...partnerEntries, { partner_id: 0, contact_name: '', contact_phone: '', contact_email: '' }]);
  }

  function removePartner(idx: number) {
    setPartnerEntries(partnerEntries.filter((_, i) => i !== idx));
  }

  function updatePartner(idx: number, field: keyof PartnerEntry, value: string | number) {
    setPartnerEntries(partnerEntries.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  }

  function addContact() {
    setContactEntries([...contactEntries, { name: '', phone: '', email: '' }]);
  }

  function removeContact(idx: number) {
    setContactEntries(contactEntries.filter((_, i) => i !== idx));
  }

  function updateContact(idx: number, field: keyof ContactEntry, value: string) {
    setContactEntries(contactEntries.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  }

  async function addVolTeam() {
    if (!newTeamName.trim() || !activitySlug) return;
    try {
      const team = await staffVolunteerTeamsService.create(activitySlug, {
        name: newTeamName.trim(),
        icon: newTeamIcon || undefined,
        color: newTeamColor || undefined,
        capacity: newTeamCapacity ? parseInt(newTeamCapacity) : undefined,
      });
      setVolTeams([...volTeams, team]);
      setNewTeamName('');
      setNewTeamIcon('');
      setNewTeamColor('#3b0b86');
      setNewTeamCapacity('');
    } catch { setError('Eroare la adăugarea echipei.'); }
  }

  async function removeVolTeam(id: number) {
    if (!activitySlug) return;
    try {
      await staffVolunteerTeamsService.delete(activitySlug, id);
      setVolTeams(volTeams.filter((t) => t.id !== id));
    } catch { setError('Eroare la ștergerea echipei.'); }
  }

  async function addCoordinator(userId: number) {
    if (!activitySlug) return;
    try {
      const coord = await staffCoordinatorsService.add(activitySlug, userId);
      setCoordinators([...coordinators, coord]);
      setCoordSearch('');
      setCoordDropdownOpen(false);
    } catch { setError('Eroare la adăugarea coordonatorului.'); }
  }

  async function removeCoordinator(id: number) {
    if (!activitySlug) return;
    try {
      await staffCoordinatorsService.remove(activitySlug, id);
      setCoordinators(coordinators.filter((c) => c.id !== id));
    } catch { setError('Eroare la eliminarea coordonatorului.'); }
  }

  function addParticipantType() {
    setParticipantTypes((prev) => {
      const noneDefault = prev.every((t) => !t.is_default);
      const next: ParticipantTypeEntry = {
        title: '',
        description: '',
        limit: '',
        price: '0',
        is_default: noneDefault,
        sort_order: prev.length,
        confirmed_count: 0,
      };
      return [...prev, next];
    });
  }

  function removeParticipantType(idx: number) {
    setParticipantTypes((prev) => {
      const target = prev[idx];
      if (!target || target.confirmed_count > 0) return prev;
      const next = prev.filter((_, i) => i !== idx).map((t, i) => ({ ...t, sort_order: i }));
      // Ensure exactly one default remains: if we removed the default, mark the first row.
      if (target.is_default && next.length > 0 && next.every((t) => !t.is_default)) {
        next[0] = { ...next[0], is_default: true };
      }
      return next;
    });
  }

  function moveParticipantType(idx: number, delta: -1 | 1) {
    setParticipantTypes((prev) => {
      const target = idx + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((t, i) => ({ ...t, sort_order: i }));
    });
  }

  function updateParticipantType<K extends keyof ParticipantTypeEntry>(
    idx: number,
    field: K,
    value: ParticipantTypeEntry[K]
  ) {
    setParticipantTypes((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t))
    );
  }

  function setDefaultParticipantType(idx: number) {
    setParticipantTypes((prev) => prev.map((t, i) => ({ ...t, is_default: i === idx })));
  }

  function toggleTag(tagId: number) {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  }

  async function handleSave() {
    if (!title.trim() || !startDatetime) {
      setError('Titlul și data de start sunt obligatorii.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const showParticipantTypes = requiresRegistration && registrationType === 'internal';
      const anyPaidType = participantTypes.some((t) => parseFloat(t.price || '0') > 0);

      const payload: Record<string, unknown> = {
        title,
        description,
        start_datetime: new Date(startDatetime).toISOString(),
        end_datetime: endDatetime ? new Date(endDatetime).toISOString() : null,
        location: locationCoords ? { type: 'Point', coordinates: [locationCoords.lng, locationCoords.lat] } : null,
        location_text: locationText,
        tag_ids: selectedTagIds,
        activity_partners_data: partnerEntries.filter((p) => p.partner_id > 0),
        contacts_data: contactEntries.filter((c) => c.name.trim()),
        participant_limit: participantLimit ? parseInt(participantLimit) : null,
        min_age: minAge ? parseInt(minAge) : null,
        max_age: maxAge ? parseInt(maxAge) : null,
        minors_require_adult: minorsRequireAdult,
        requires_registration: requiresRegistration,
        is_invitation_only: isInvitationOnly,
        registration_type: registrationType,
        registration_deadline: registrationDeadline ? new Date(registrationDeadline).toISOString() : null,
        registration_link: registrationType === 'external' ? registrationLink : '',
        registration_fields: registrationType === 'internal' ? registrationFields : {},
        volunteer_applications_open: volunteerApplicationsOpen,
        volunteer_capacity: volunteerCapacity ? parseInt(volunteerCapacity) : null,
        volunteer_guidance: volunteerGuidance,
        is_volunteer_visible: isVolunteerVisible,
        is_published: isPublished,
        is_featured: isFeatured,
        payment_methods: showParticipantTypes && anyPaidType ? paymentMethods : '',
      };

      if (showParticipantTypes) {
        payload.participant_types_data = participantTypes.map((t, i) => ({
          id: t.id,
          title: t.title.trim(),
          description: t.description,
          limit: t.limit ? parseInt(t.limit) : null,
          price: parseFloat(t.price || '0').toFixed(2),
          is_default: t.is_default,
          sort_order: i,
        }));
      }

      if (isEdit) {
        if (isPartnerView) {
          await partnerActivitiesService.update(id!, payload);
        } else {
          await staffActivitiesService.update(id!, payload);
        }
        if (bannerFile) {
          await staffActivitiesService.uploadBanner(id!, bannerFile);
        }
      } else {
        const result = isPartnerView
          ? await partnerActivitiesService.create(payload)
          : await staffActivitiesService.create(payload);
        if (bannerFile) {
          await staffActivitiesService.uploadBanner(result.id, bannerFile);
        }
      }

      navigate(isPartnerView ? '/staff/activitatile-mele' : '/staff/activitati');
    } catch (e) {
      const err = e as { body?: unknown; status?: number };
      setError(formatApiError(err?.body, err?.status));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  const mainTags = allTags.filter((t) => t.is_main_tag);
  const normalTags = allTags.filter((t) => !t.is_main_tag);
  const usedPartnerIds = partnerEntries.map((p) => p.partner_id);

  const inputClass = "w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20";

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate(isPartnerView ? '/staff/activitatile-mele' : '/staff/activitati')}
          className="rounded-lg p-2 text-muted-foreground hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          {isEdit ? 'Editează Activitate' : 'Activitate Nouă'}
        </h1>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="space-y-6">
        {/* Basic Info */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Informații de bază</h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">Titlu *</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} autoFocus />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium">Data & ora start *</label>
                <DateTimeInput value={startDatetime} onChange={setStartDatetime} className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Data & ora final</label>
                <DateTimeInput value={endDatetime} onChange={setEndDatetime} className={inputClass} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Locație</label>
              <input type="text" value={locationText} onChange={(e) => setLocationText(e.target.value)} placeholder="Ex: Cetatea Alba Carolina" className={inputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Locație pe hartă</label>
              <LocationPicker value={locationCoords} onChange={setLocationCoords} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Banner</label>
              <div className="flex items-center gap-4">
                {bannerPreview && <img src={bannerPreview} alt="" className="h-20 rounded-lg border object-cover" />}
                <input type="file" accept="image/*" onChange={handleBannerChange} className="text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--vivid-purple)]/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[var(--vivid-purple)]" />
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Descriere (Markdown)</h2>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showPreview ? 'Editare' : 'Previzualizare'}
              </button>
              <MarkdownHelpButton />
            </div>
          </div>
          {showPreview ? (
            <div className="min-h-[160px] rounded-lg border bg-gray-50 px-3 py-2.5 text-sm" dangerouslySetInnerHTML={{
              __html: description
                .replace(/\n/g, '<br>')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .replace(/^## (.*$)/gm, '<h3 style="font-weight:bold;font-size:1.1em;margin:8px 0 4px">$1</h3>')
                .replace(/^# (.*$)/gm, '<h2 style="font-weight:bold;font-size:1.25em;margin:10px 0 4px">$1</h2>'),
            }} />
          ) : (
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={8} className={inputClass} style={{ fontFamily: 'var(--font-secondary)' }} />
          )}
        </section>

        {/* Tags */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Tag-uri</h2>
          {mainTags.length > 0 && (
            <div className="mb-3">
              <label className="mb-2 block text-xs font-semibold uppercase text-muted-foreground">Tag-uri principale</label>
              <div className="flex flex-wrap gap-2">
                {mainTags.map((t) => (
                  <button key={t.id} type="button" onClick={() => toggleTag(t.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${selectedTagIds.includes(t.id) ? 'border-transparent' : 'border-gray-200 bg-white'}`}
                    style={selectedTagIds.includes(t.id) ? { backgroundColor: t.color || '#3b0b86', color: getTextColorHex(t.color || '#3b0b86') } : {}}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {normalTags.length > 0 && (
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase text-muted-foreground">Alte tag-uri</label>
              <div className="flex flex-wrap gap-2">
                {normalTags.map((t) => (
                  <button key={t.id} type="button" onClick={() => toggleTag(t.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${selectedTagIds.includes(t.id) ? 'bg-[var(--vivid-purple)] text-white border-transparent' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Partners */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Prieteni</h2>
          <div className="mb-4">
            <PartnerMultiSelect
              partners={allPartners}
              selectedIds={usedPartnerIds}
              excludeIds={usedPartnerIds}
              onChange={(id) => {
                setPartnerEntries([...partnerEntries, { partner_id: id, contact_name: '', contact_phone: '', contact_email: '' }]);
              }}
            />
          </div>
          <div className="space-y-4">
            {partnerEntries.map((pe, idx) => {
              const partner = allPartners.find((p) => p.id === pe.partner_id);
              return (
              <div key={idx} className="rounded-lg border bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium">{partner?.name || 'Prieten necunoscut'}</span>
                  <button type="button" onClick={() => removePartner(idx)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <input type="text" value={pe.contact_name} onChange={(e) => updatePartner(idx, 'contact_name', e.target.value)} placeholder="Persoană contact" className={inputClass} />
                  <input type="tel" value={pe.contact_phone} onChange={(e) => updatePartner(idx, 'contact_phone', e.target.value)} placeholder="Telefon" className={inputClass} />
                  <input type="email" value={pe.contact_email} onChange={(e) => updatePartner(idx, 'contact_email', e.target.value)} placeholder="Email" className={inputClass} />
                </div>
              </div>
            );
            })}
          </div>
        </section>

        {/* Contacts */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Contacte activitate</h2>
            <button type="button" onClick={addContact} className="flex items-center gap-1 text-sm font-medium text-[var(--vivid-purple)] hover:underline">
              <Plus className="h-4 w-4" /> Adaugă
            </button>
          </div>
          {contactEntries.length === 0 && (
            <p className="text-sm text-muted-foreground">Niciun contact adăugat.</p>
          )}
          <div className="space-y-3">
            {contactEntries.map((ce, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="grid flex-1 gap-3 sm:grid-cols-3">
                  <input type="text" value={ce.name} onChange={(e) => updateContact(idx, 'name', e.target.value)} placeholder="Nume *" className={inputClass} />
                  <input type="tel" value={ce.phone} onChange={(e) => updateContact(idx, 'phone', e.target.value)} placeholder="Telefon" className={inputClass} />
                  <input type="email" value={ce.email} onChange={(e) => updateContact(idx, 'email', e.target.value)} placeholder="Email" className={inputClass} />
                </div>
                <button type="button" onClick={() => removeContact(idx)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Registration & Limits */}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Înregistrare & Limite</h2>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium">Limită participanți</label>
                <input type="number" value={participantLimit} onChange={(e) => setParticipantLimit(e.target.value)} min="0" className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Vârstă minimă</label>
                <input type="number" value={minAge} onChange={(e) => setMinAge(e.target.value)} min="0" className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">Vârstă maximă</label>
                <input type="number" value={maxAge} onChange={(e) => setMaxAge(e.target.value)} min="0" className={inputClass} />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Minorii necesită adult</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Participanții sub 18 ani trebuie să fie însoțiți de un adult. Va fi afișat un avertisment pe pagina activității.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={minorsRequireAdult} onChange={(e) => setMinorsRequireAdult(e.target.checked)} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-[var(--vivid-purple)] peer-checked:after:translate-x-full" />
                </label>
              </div>
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Necesită înregistrare</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Participanții trebuie să se înregistreze înainte de eveniment. Activează pentru a configura deadline-ul și link-ul de înregistrare.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={requiresRegistration} onChange={(e) => setRequiresRegistration(e.target.checked)} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-[var(--vivid-purple)] peer-checked:after:translate-x-full" />
                </label>
              </div>
            </div>
            {requiresRegistration && !isPartnerView && (
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Doar cu invitație</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Doar persoanele invitate pot participa. Trimite invitații din pagina activității după salvare.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={isInvitationOnly} onChange={(e) => setIsInvitationOnly(e.target.checked)} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-[var(--vivid-purple)] peer-checked:after:translate-x-full" />
                </label>
              </div>
            )}
            {requiresRegistration && (
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium">Tip înregistrare</label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors ${registrationType === 'internal' ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      <input
                        type="radio"
                        name="registration_type"
                        value="internal"
                        checked={registrationType === 'internal'}
                        onChange={() => setRegistrationType('internal')}
                        className="mt-0.5 accent-[var(--vivid-purple)]"
                      />
                      <div>
                        <span className="text-sm font-medium">Sistem intern</span>
                        <p className="mt-0.5 text-xs text-muted-foreground">Utilizatorii se înregistrează prin platforma noastră. Datele sunt gestionate de noi.</p>
                      </div>
                    </label>
                    <label
                      className={`flex items-start gap-3 rounded-lg border p-4 transition-colors ${
                        registrationCount > 0 && registrationType === 'internal'
                          ? 'cursor-not-allowed border-gray-200 opacity-50'
                          : `cursor-pointer ${registrationType === 'external' ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5' : 'border-gray-200 hover:bg-gray-50'}`
                      }`}
                    >
                      <input
                        type="radio"
                        name="registration_type"
                        value="external"
                        checked={registrationType === 'external'}
                        disabled={registrationCount > 0 && registrationType === 'internal'}
                        onChange={() => setRegistrationType('external')}
                        className="mt-0.5 accent-[var(--vivid-purple)]"
                      />
                      <div>
                        <span className="text-sm font-medium">Link extern</span>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {registrationCount > 0 && registrationType === 'internal'
                            ? 'Nu se poate schimba — există înregistrări prin sistemul intern.'
                            : 'Utilizatorii sunt redirecționați către un formular extern. Nu gestionăm datele.'}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Deadline înregistrare</label>
                  <DateTimeInput value={registrationDeadline} onChange={setRegistrationDeadline} className={inputClass} />
                </div>
                {registrationType === 'internal' && (
                  <div>
                    <label className="mb-2 block text-sm font-medium">Câmpuri formular înregistrare</label>
                    <p className="mb-3 text-xs text-muted-foreground">
                      Prenume, nume și email sunt întotdeauna vizibile și obligatorii.
                      {registrationCount > 0 && ' Configurația câmpurilor nu poate fi modificată deoarece există înregistrări.'}
                    </p>
                    <div className="overflow-hidden rounded-lg border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="px-3 py-2 text-left font-medium">Câmp</th>
                            <th className="px-3 py-2 text-center font-medium">Vizibil</th>
                            <th className="px-3 py-2 text-center font-medium">Obligatoriu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {([
                            { key: 'phone', label: 'Telefon' },
                            { key: 'date_of_birth', label: 'Data nașterii' },
                            { key: 'gender', label: 'Gen' },
                            { key: 'source_organization', label: 'Organizația de proveniență' },
                          ] as const).map(({ key, label }) => {
                            const field = registrationFields[key] || { visible: true, required: false };
                            return (
                              <tr key={key} className="border-b last:border-b-0">
                                <td className="px-3 py-2">{label}</td>
                                <td className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={field.visible}
                                    disabled={registrationCount > 0}
                                    onChange={(e) => {
                                      const visible = e.target.checked;
                                      setRegistrationFields((prev) => ({
                                        ...prev,
                                        [key]: { visible, required: visible ? prev[key]?.required ?? false : false },
                                      }));
                                    }}
                                    className="h-4 w-4 rounded accent-[var(--vivid-purple)] disabled:opacity-30"
                                  />
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={field.required}
                                    disabled={registrationCount > 0 || !field.visible}
                                    onChange={(e) => {
                                      setRegistrationFields((prev) => ({
                                        ...prev,
                                        [key]: { ...prev[key], required: e.target.checked },
                                      }));
                                    }}
                                    className="h-4 w-4 rounded accent-[var(--vivid-purple)] disabled:opacity-30"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {registrationType === 'external' && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">Link înregistrare externă *</label>
                    <input type="url" value={registrationLink} onChange={(e) => setRegistrationLink(e.target.value)} placeholder="https://..." className={inputClass} required />
                    <p className="mt-1 text-xs text-muted-foreground">Utilizatorii vor fi avertizați că părăsesc platforma și că datele lor vor fi gestionate de organizatorul extern.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Participant Types */}
        {requiresRegistration && registrationType === 'internal' && (() => {
          const limitSum = participantTypes.reduce((acc, t) => acc + (t.limit ? parseInt(t.limit) : 0), 0);
          const overall = participantLimit ? parseInt(participantLimit) : null;
          const exceedsOverall = overall !== null && limitSum > overall;
          const anyPaidType = participantTypes.some((t) => parseFloat(t.price || '0') > 0);
          return (
            <section className="rounded-xl border bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
                Tipuri de participanți
              </h2>
              <p className="mb-4 text-xs text-muted-foreground">
                Configurează unul sau mai multe tipuri (ex: Standard, VIP, Voluntar internațional). Fiecare tip are propria limită și preț. Plățile se gestionează direct cu organizatorul — noi nu procesăm plăți.
              </p>
              {participantTypes.length === 0 && (
                <div className="mb-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-xs text-muted-foreground">
                  Niciun tip configurat. Apasă „Adaugă tip” — la salvare, dacă lași gol, se va crea automat un tip implicit „Participant”.
                </div>
              )}
              <div className="space-y-3">
                {participantTypes.map((t, idx) => {
                  const locked = t.confirmed_count > 0;
                  const fieldsDisabled = locked;
                  return (
                    <div
                      key={t.id ?? `new-${idx}`}
                      className={`rounded-lg border p-4 ${t.is_default ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5' : 'border-gray-200'}`}
                    >
                      <div className="mb-3 flex items-center gap-2">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => moveParticipantType(idx, -1)}
                            disabled={idx === 0}
                            className="rounded p-1 text-muted-foreground hover:bg-gray-100 disabled:opacity-30"
                            aria-label="Mută în sus"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveParticipantType(idx, 1)}
                            disabled={idx === participantTypes.length - 1}
                            className="rounded p-1 text-muted-foreground hover:bg-gray-100 disabled:opacity-30"
                            aria-label="Mută în jos"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <label className="flex flex-1 items-center gap-2 text-xs">
                          <input
                            type="radio"
                            name="participant_type_default"
                            checked={t.is_default}
                            onChange={() => setDefaultParticipantType(idx)}
                            className="accent-[var(--vivid-purple)]"
                          />
                          <span className="font-medium">Implicit</span>
                        </label>
                        {locked && (
                          <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                            <Lock className="h-3 w-3" /> {t.confirmed_count} înscriși
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeParticipantType(idx)}
                          disabled={locked}
                          className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-30"
                          aria-label="Șterge tipul"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="sm:col-span-2">
                          <label className="mb-1 block text-xs font-medium">Titlu *</label>
                          <input
                            type="text"
                            value={t.title}
                            onChange={(e) => updateParticipantType(idx, 'title', e.target.value)}
                            disabled={fieldsDisabled}
                            placeholder="ex: Participant, VIP, Voluntar internațional"
                            className={`${inputClass} disabled:bg-gray-50 disabled:text-muted-foreground`}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium">Limită (gol = nelimitat)</label>
                          <input
                            type="number"
                            value={t.limit}
                            onChange={(e) => updateParticipantType(idx, 'limit', e.target.value)}
                            disabled={fieldsDisabled}
                            min="0"
                            className={`${inputClass} disabled:bg-gray-50 disabled:text-muted-foreground`}
                          />
                        </div>
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-3">
                        <div>
                          <label className="mb-1 block text-xs font-medium">Preț (RON)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={t.price}
                            onChange={(e) => updateParticipantType(idx, 'price', e.target.value)}
                            disabled={fieldsDisabled}
                            className={`${inputClass} disabled:bg-gray-50 disabled:text-muted-foreground`}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <div className="mb-1 flex items-center justify-between">
                            <label className="text-xs font-medium">Descriere (Markdown)</label>
                            <MarkdownHelpButton />
                          </div>
                          <textarea
                            value={t.description}
                            onChange={(e) => updateParticipantType(idx, 'description', e.target.value)}
                            disabled={fieldsDisabled}
                            rows={2}
                            className={`${inputClass} disabled:bg-gray-50 disabled:text-muted-foreground`}
                            style={{ fontFamily: 'var(--font-secondary)' }}
                          />
                        </div>
                      </div>
                      {locked && (
                        <p className="mt-2 text-xs text-amber-700">
                          Tipul are participanți înregistrați. Doar ordinea și tipul implicit pot fi modificate.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              {exceedsOverall && (
                <p className="mt-3 text-xs text-amber-700">
                  Suma limitelor pe tipuri ({limitSum}) depășește limita totală a evenimentului ({overall}).
                </p>
              )}
              <button
                type="button"
                onClick={addParticipantType}
                className="mt-4 flex items-center gap-2 rounded-lg border border-dashed border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5 px-3 py-2 text-sm font-medium text-[var(--vivid-purple)] hover:bg-[var(--vivid-purple)]/10"
              >
                <Plus className="h-4 w-4" /> Adaugă tip
              </button>

              {anyPaidType && (
                <div className="mt-6 border-t pt-5">
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-sm font-medium">Metode de plată</label>
                    <MarkdownHelpButton />
                  </div>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Plățile se gestionează direct cu organizatorul. Descrie metodele de plată acceptate (transfer bancar, numerar la fața locului, etc.).
                  </p>
                  <textarea
                    value={paymentMethods}
                    onChange={(e) => setPaymentMethods(e.target.value)}
                    rows={4}
                    className={inputClass}
                    style={{ fontFamily: 'var(--font-secondary)' }}
                    placeholder="Ex: Transfer bancar IBAN ROXX… sau numerar la sosirea pe locație."
                  />
                </div>
              )}
            </section>
          );
        })()}

        {/* Volunteer Settings */}
        {!isPartnerView && (
          <section className="rounded-xl border bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Voluntari</h2>
            <div className="space-y-4">
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Deschis pentru aplicații voluntari</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">Voluntarii pot aplica pentru această activitate. Poți închide aplicațiile oricând.</p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input type="checkbox" checked={volunteerApplicationsOpen} onChange={(e) => setVolunteerApplicationsOpen(e.target.checked)} className="peer sr-only" />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-[var(--vivid-purple)] peer-checked:after:translate-x-full" />
                </label>
              </div>
              {volunteerApplicationsOpen && (
                <>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">Capacitate voluntari</label>
                    <input type="number" value={volunteerCapacity} onChange={(e) => setVolunteerCapacity(e.target.value)} min="0" placeholder="Nelimitat" className={inputClass} />
                    <p className="mt-1 text-xs text-muted-foreground">Numărul maxim de voluntari aprobați. Lasă gol pentru nelimitat.</p>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">Instrucțiuni pentru voluntari</label>
                    <textarea value={volunteerGuidance} onChange={(e) => setVolunteerGuidance(e.target.value)} rows={3} className={inputClass} placeholder="Informații utile pentru voluntari: program, ce trebuie să aducă, etc." style={{ fontFamily: 'var(--font-secondary)' }} />
                  </div>

                  {/* Volunteer Teams & Coordinators — only for existing activities */}
                  {!isEdit && (
                    <p className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 px-4 py-3 text-xs text-amber-700" style={{ fontFamily: 'var(--font-secondary)' }}>
                      Salvează activitatea mai întâi pentru a putea adăuga echipe de voluntari și coordonatori.
                    </p>
                  )}
                  {isEdit && activitySlug && (
                    <div>
                      <label className="mb-2 block text-sm font-medium">Echipe voluntari</label>
                      {volTeams.length > 0 && (
                        <div className="mb-3 space-y-2">
                          {volTeams.map((t) => (
                            <div key={t.id} className="flex items-center justify-between rounded-lg border bg-gray-50 px-3 py-2.5">
                              <div className="flex items-center gap-2.5">
                                <div
                                  className="flex h-7 w-7 items-center justify-center rounded-md"
                                  style={{ backgroundColor: t.color ? `${t.color}20` : '#f0f0f0' }}
                                >
                                  {t.icon ? (
                                    <IconByName name={t.icon} className="h-4 w-4" style={{ color: t.color || '#666' }} />
                                  ) : (
                                    <Users className="h-4 w-4" style={{ color: t.color || '#666' }} />
                                  )}
                                </div>
                                <div>
                                  <span className="text-sm font-medium">{t.name}</span>
                                  {t.capacity != null && (
                                    <span className="ml-2 text-xs text-muted-foreground">
                                      ({t.confirmed_count}/{t.capacity} locuri)
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button type="button" onClick={() => removeVolTeam(t.id)} className="rounded-lg p-1 text-red-500 hover:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="rounded-lg border bg-gray-50/50 p-3 space-y-3">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[3fr_1fr]">
                          <input
                            type="text"
                            value={newTeamName}
                            onChange={(e) => setNewTeamName(e.target.value)}
                            placeholder="Nume echipă"
                            className={inputClass}
                          />
                          <input
                            type="number"
                            value={newTeamCapacity}
                            onChange={(e) => setNewTeamCapacity(e.target.value)}
                            placeholder="Capacitate"
                            min="0"
                            className={inputClass}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <label className="mb-1 block text-xs text-muted-foreground">Iconiță</label>
                            <IconPicker value={newTeamIcon} onChange={setNewTeamIcon} />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs text-muted-foreground">Culoare</label>
                            <input
                              type="color"
                              value={newTeamColor}
                              onChange={(e) => setNewTeamColor(e.target.value)}
                              className="h-[42px] w-[42px] cursor-pointer rounded-lg border p-1"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={addVolTeam}
                            disabled={!newTeamName.trim()}
                            className="flex h-[42px] items-center gap-1.5 rounded-lg bg-[var(--vivid-purple)] px-4 text-sm font-medium text-white disabled:opacity-40"
                          >
                            <Plus className="h-4 w-4" /> Adaugă
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Coordinators — only for existing activities */}
                  {isEdit && activitySlug && (
                    <div>
                      <label className="mb-2 block text-sm font-medium">Coordonatori</label>
                      <p className="mb-2 text-xs text-muted-foreground">Coordonatorii primesc notificări la aplicații noi și pot aproba/respinge voluntari.</p>
                      {coordinators.length > 0 && (
                        <div className="mb-3 space-y-2">
                          {coordinators.map((c) => (
                            <div key={c.id} className="flex items-center justify-between rounded-lg border bg-gray-50 px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-[var(--vivid-purple)]" />
                                <span className="text-sm font-medium">{c.user_first_name} {c.user_last_name}</span>
                                <span className="text-xs text-muted-foreground">({c.user_email})</span>
                              </div>
                              <button type="button" onClick={() => removeCoordinator(c.id)} className="rounded-lg p-1 text-red-500 hover:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="relative">
                        <input
                          type="text"
                          value={coordSearch}
                          onChange={(e) => { setCoordSearch(e.target.value); setCoordDropdownOpen(true); }}
                          onFocus={() => setCoordDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setCoordDropdownOpen(false), 200)}
                          placeholder="Caută coordonator după nume sau email..."
                          className={inputClass}
                        />
                        {coordDropdownOpen && coordSearch.length >= 1 && (() => {
                          const q = coordSearch.toLowerCase();
                          const matches = staffUsers.filter((u) => {
                            if (coordinators.some((c) => c.user === u.id)) return false;
                            const full = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
                            return full.includes(q);
                          });
                          if (matches.length === 0) return null;
                          return (
                            <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-white shadow-lg">
                              {matches.slice(0, 8).map((u) => (
                                <button
                                  key={u.id}
                                  type="button"
                                  onClick={() => addCoordinator(u.id)}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                                >
                                  <Users className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                                  <span className="font-medium">{u.first_name} {u.last_name}</span>
                                  <span className="text-xs text-muted-foreground">{u.email}</span>
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 space-y-3">
            {!isPartnerView && (
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Vizibil pentru voluntari</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Voluntarii pot vedea activitatea în lista de evenimente, chiar dacă nu e publicată public.
                  </p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={isVolunteerVisible || isPublished}
                    disabled={isPublished}
                    onChange={(e) => setIsVolunteerVisible(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className={`h-6 w-11 rounded-full after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all ${isPublished ? 'bg-green-400 opacity-50' : 'bg-gray-300'} peer-checked:bg-green-500 peer-checked:after:translate-x-full`} />
                </label>
              </div>
            )}
            {!isPartnerView && (
              <div className="flex items-start justify-between rounded-lg border p-4">
                <div className="flex-1">
                  <span className="text-sm font-medium">Eveniment important</span>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Apare primul pe homepage, înaintea evenimentelor cronologice. Folosește pentru summit-uri, lansări, evenimente majore.
                  </p>
                </div>
                <label className="relative ml-4 inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-[var(--vivid-yellow-green)] peer-checked:after:translate-x-full" />
                </label>
              </div>
            )}
            <div className="flex items-start justify-between rounded-lg border p-4">
              <div className="flex-1">
                <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                  {isPublished ? 'Publicat' : 'Draft'}
                </span>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {isPartnerView
                    ? 'Doar echipa staff poate publica sau retrage o activitate.'
                    : isPublished ? 'Activitatea este vizibilă public pe site.' : 'Activitatea este salvată ca ciornă și nu este vizibilă public.'}
                </p>
              </div>
              <label className={`relative ml-4 inline-flex items-center ${isPartnerView ? 'opacity-50' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={isPublished}
                  disabled={isPartnerView}
                  onChange={(e) => {
                    setIsPublished(e.target.checked);
                    if (e.target.checked) setIsVolunteerVisible(true);
                  }}
                  className="peer sr-only"
                />
                <div className="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full" />
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => navigate(isPartnerView ? '/staff/activitatile-mele' : '/staff/activitati')}
              className="rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
